import {
  ReportFilterEntry,
  ReportPatientReportType,
  ReportTimeInterval,
} from '@common/types';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useReportStore } from '@store';
import { Select } from '@ui';
import { Add } from '@mui/icons-material';
import { FilterEntry, FilterPopover } from './Filters.components.tsx';
import { useState } from 'react';
import { theme } from '@store';
import {
  patientReportTypeOptions,
  reportTimeIntervalOptions,
} from '../../../types.ts';

export const Filters = () => {
  const { filters = [], setFilters, report, setReport } = useReportStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSaveFilter = (entry: ReportFilterEntry) => {
    setFilters([...filters.filter(o => o.field !== entry.field), entry]);
  };

  const showDateError =
    report?.interval === 'custom' &&
    !report?.custom_timeframe?.from_date &&
    !report?.custom_timeframe?.to_date;

  const filtersLength =
    filters.length +
    (['patients_report', 'tasks_report'].includes(report?.report_type ?? '')
      ? 2
      : 1);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '260px',
        minWidth: '260px',
        gap: '1rem',
        pt: '1rem',
      }}
    >
      <Typography>{`Filters (${filtersLength})`}</Typography>
      {report?.report_type === 'patients_report' && (
        <Select
          name='patient_report_type'
          clearable={false}
          options={patientReportTypeOptions}
          value={report?.options?.patient_report_type}
          label='Show'
          sx={{
            bgcolor: theme.palette.common.white,
          }}
          onChange={event => {
            setReport({
              ...report,
              options: {
                ...report?.options,
                patient_report_type: event.target
                  .value as ReportPatientReportType,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any,
            });
          }}
        />
      )}
      <Select
        name='interval'
        clearable={false}
        options={reportTimeIntervalOptions}
        value={report?.interval}
        label='Time range'
        sx={{
          bgcolor: theme.palette.common.white,
        }}
        onChange={event => {
          setReport({
            ...report,
            interval: event.target.value as ReportTimeInterval,
          });
        }}
      />
      {report?.interval === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
          <DatePicker
            name='from_date'
            slotProps={{
              field: {
                clearable: true,
              },
              textField: {
                error: showDateError,
                helperText: showDateError
                  ? 'Please select at least 1 date'
                  : '',
                sx: {
                  '& .MuiInputBase-root': {
                    bgcolor: theme.palette.common.white,
                  },
                },
              },
            }}
            label='From'
            value={
              report?.custom_timeframe?.from_date
                ? dayjs(report.custom_timeframe.from_date)
                : null
            }
            maxDate={dayjs(report?.custom_timeframe?.to_date).add(-1, 'day')}
            onChange={val => {
              const formattedValue = val?.isValid()
                ? dayjs(val).format('YYYY-MM-DD')
                : null;

              setReport({
                ...report,
                custom_timeframe: {
                  ...report?.custom_timeframe,
                  from_date: formattedValue,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
              });
            }}
          />
          <DatePicker
            name='to_date'
            slotProps={{
              textField: {
                error: showDateError,
                helperText: showDateError
                  ? 'Please select at least 1 date'
                  : '',
                sx: {
                  '& .MuiInputBase-root': {
                    bgcolor: theme.palette.common.white,
                  },
                },
              },
            }}
            label='To'
            value={
              report?.custom_timeframe?.to_date
                ? dayjs(report.custom_timeframe.to_date)
                : null
            }
            minDate={dayjs(report?.custom_timeframe?.from_date).add(1, 'day')}
            onChange={val => {
              const formattedValue = val?.isValid()
                ? dayjs(val).format('YYYY-MM-DD')
                : null;

              setReport({
                ...report,
                custom_timeframe: {
                  ...report?.custom_timeframe,
                  to_date: formattedValue,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
              });
            }}
          />
        </LocalizationProvider>
      )}
      {report?.report_type === 'tasks_report' && (
        <FormControlLabel
          sx={{ width: 'fit-content' }}
          control={
            <Checkbox
              name='include_future_tasks'
              sx={{ py: 0 }}
              checked={!!report?.options?.include_future_tasks}
              onChange={event => {
                setReport({
                  ...report,
                  options: {
                    ...report?.options,
                    include_future_tasks: event.target.checked,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any,
                });
              }}
            />
          }
          label='Include future tasks'
        />
      )}
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Only fields marked with an asterisk (*) are affected by the time range
        filter.
      </Typography>
      {filters.map((filter, index) => (
        <FilterEntry key={`${filter.field}-${index}`} entry={filter} />
      ))}

      <Button
        sx={{ height: '30px', width: 'fit-content' }}
        color='primary'
        startIcon={<Add sx={{ height: '20px' }} color='primary' />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        ADD
      </Button>

      {/* Create filter popover */}
      {anchorEl && (
        <FilterPopover
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          handleApply={handleSaveFilter}
        />
      )}
    </Box>
  );
};
